/*  LearningAcademyCatalogue — Level 1.
    ────────────────────────────────────────────────────────────────────
    Restored the earlier premium catalogue aesthetic (rose-tinted hero,
    filter tabs, rich course cards with icon tiles + status pills +
    meta chips) while keeping the DB-driven course fetch from
    /api/academy/published-courses.

    Placeholder "Coming Soon" cards (Advanced Smile Design + Practice
    Growth Accelerator) keep the grid feeling premium until more DB
    courses ship.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    GraduationCap, Clock, Books, ArrowSquareOut, Sparkle, Tooth,
    ChartLineUp, Trophy, Stack, Lock, CheckCircle,
    Certificate, Warning, DownloadSimple, Hourglass, CalendarBlank,
    Share, VideoCamera, Users, Broadcast, PlayCircle, ClockCounterClockwise,
    ArrowsDownUp, Fire, MagnifyingGlass,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuRadioGroup, DropdownMenuRadioItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import PaymentPendingDialog from "@/components/doctor/PaymentPendingDialog";
import RetakeCourseDialog from "@/components/doctor/RetakeCourseDialog";


/* iter101.87 · Chip config for the canonical FilterChipRow. Order,
   label, and Icon are the source of truth here; counts are computed
   inside the component from `allCards` + `certificates`. Tones map to
   the FilterChipRow tone palette (primary/rose/emerald/amber/sky/slate/
   violet) — kept close to the retired custom-tab colours so the tab
   flip doesn't feel like a redesign. */
const ACADEMY_CHIPS = [
    { id: "all",            label: "All",            tone: "primary" },
    { id: "recommended",    label: "Recommended",    tone: "rose",    Icon: Sparkle },
    { id: "live_sessions",  label: "Live Sessions",  tone: "rose",    Icon: Broadcast, showCount: false },
    { id: "in_progress",    label: "In Progress",    tone: "rose" },
    { id: "completed",      label: "Completed",      tone: "emerald" },
    { id: "certifications", label: "Certifications", tone: "amber",   Icon: Trophy },
    { id: "coming_soon",    label: "Coming Soon",    tone: "slate" },
];

/* iter101.90 · Sort options adapt per filter chip. On course-grid
   tabs (All / Recommended / In Progress / Completed / Coming Soon)
   we sort courses. On Certifications we sort certificates. On Live
   Sessions we sort webinars. The dropdown label + option list flip
   when the chip flips, and `sortBy` is auto-reset to the new tab's
   default in an effect below so a stale value never lingers. */
const ACADEMY_SORT_OPTIONS_BY_FILTER = {
    // Course-grid tabs share the same sort menu.
    all:            "courses",
    recommended:    "courses",
    in_progress:    "courses",
    completed:      "courses",
    coming_soon:    "courses",
    certifications: "certifications",
    live_sessions:  "live_sessions",
};

const ACADEMY_SORT_MENUS = {
    courses: [
        { id: "newest",      label: "Newest first" },
        { id: "title-asc",   label: "Title A → Z" },
        { id: "title-desc",  label: "Title Z → A" },
        { id: "shortest",    label: "Shortest first" },
        { id: "longest",     label: "Longest first" },
    ],
    certifications: [
        { id: "issued-desc",  label: "Newest issued" },
        { id: "issued-asc",   label: "Oldest issued" },
        { id: "expires-asc",  label: "Expiring soonest" },
        { id: "title-asc",    label: "Title A → Z" },
    ],
    live_sessions: [
        { id: "soonest",      label: "Soonest first" },
        { id: "furthest",     label: "Furthest first" },
        { id: "newest",       label: "Newest posted" },
    ],
};

/* iter101.90 · Placeholder text adapts per tab so the input hints
   what you can search for on the current surface. */
const SEARCH_PLACEHOLDERS = {
    default:        "Search by title, track, phase…",
    certifications: "Search certificates by course title…",
    live_sessions:  "Search live sessions by title or presenter…",
};

function AcademySortMenu({ sortBy, setSortBy, filter }) {
    const menuKey = ACADEMY_SORT_OPTIONS_BY_FILTER[filter] || "courses";
    const options = ACADEMY_SORT_MENUS[menuKey];
    const label = options.find((s) => s.id === sortBy)?.label || options[0].label;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
                    title="Sort"
                    data-testid="academy-sort-btn"
                >
                    <ArrowsDownUp size={12} /> {label}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Sort by
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
                    {options.map((opt) => (
                        <DropdownMenuRadioItem
                            key={opt.id}
                            value={opt.id}
                            className="text-xs"
                            data-testid={`academy-sort-${opt.id}`}
                        >
                            {opt.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

const STATUS_META = {
    available:   { label: "Available",   cls: "bg-emerald-50 text-emerald-800 border border-emerald-200" },
    in_progress: { label: "In Progress", cls: "bg-rose-50 text-rose-700 border border-rose-200" },
    completed:   { label: "Completed",   cls: "bg-slate-100 text-slate-700 border border-slate-200" },
    locked:      { label: "Locked",      cls: "bg-slate-100 text-slate-600 border border-slate-200" },
    coming_soon: { label: "Coming Soon", cls: "bg-amber-50 text-amber-800 border border-amber-200" },
};

const CERT_VALIDITY_DAYS = 365;

/* iter101.89 · Trending threshold — a course is marked "trending" when
   at least this many doctors have enrolled in the trailing 30 days.
   Kept as a frontend-only constant so ops can tune the visibility
   bar without a backend redeploy. Server always returns the raw
   count via `trending_enroll_count_30d`. */
const TRENDING_ENROLL_THRESHOLD = 30;

/*  Aspirational placeholder cards — kept for visual richness until
    more DB courses publish. Zero backend coupling. */
const COMING_SOON_COURSES = [
    {
        id: "smile_design",
        title: "Advanced Smile Design",
        category: "Clinical Skills",
        description: "Digital smile design principles applied to real patient cases using Invisique™ workflows.",
        phases: 3, modules: 12, duration: "~10 hrs",
        icon: Tooth,
        accent: { tile: "bg-teal-50 text-teal-700 border border-teal-200" },
        tag: "Clinical",
        status: "coming_soon",
    },
    {
        id: "practice_growth",
        title: "Practice Growth Accelerator",
        category: "Practice Growth",
        description: "Marketing, consultation conversion, and pricing strategies to grow your clear aligner revenue.",
        phases: 2, modules: 8, duration: "~6 hrs",
        icon: ChartLineUp,
        accent: { tile: "bg-amber-50 text-amber-700 border border-amber-200" },
        tag: "Business",
        status: "coming_soon",
    },
];


export default function LearningAcademyCatalogue() {
    const [dbCourses, setDbCourses] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [certificates, setCertificates] = useState([]);
    const [upcomingWebinarCount, setUpcomingWebinarCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState("newest");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [courses, enrs, certs, webinars] = await Promise.all([
                api.get("/academy/published-courses"),
                api.get("/academy/my-enrollments").catch(() => ({ data: { enrollments: [] } })),
                api.get("/academy/my-certificates").catch(() => ({ data: { certificates: [] } })),
                /* iter101.88 · Fetched at parent scope so the hero
                   "Live Sessions" stat tile can render an accurate
                   count before the doctor ever clicks the Live Sessions
                   chip. The LiveSessionsPanel keeps its own fetch so
                   opening the panel later still gets fresh data. */
                api.get("/academy/webinars").catch(() => ({ data: { upcoming: [] } })),
            ]);
            setDbCourses(courses.data.courses || []);
            setEnrollments(enrs.data.enrollments || []);
            setCertificates(certs.data.certificates || []);
            setUpcomingWebinarCount((webinars.data.upcoming || []).length);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load Academy.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const prev = document.title;
        document.title = "Invisique Learning Academy";
        return () => { document.title = prev; };
    }, []);

    /* iter101.90 · Auto-reset sortBy when the filter chip changes so
       we never carry a stale option from a different tab's menu
       (e.g. "shortest" would be nonsensical on Certifications).
       Uses the first option of the new menu as the default.
       We intentionally exclude `sortBy` from the deps so this only
       fires on filter changes, not on every sort change. */
    useEffect(() => {
        const menuKey = ACADEMY_SORT_OPTIONS_BY_FILTER[filter] || "courses";
        const menu = ACADEMY_SORT_MENUS[menuKey];
        const validIds = menu.map((o) => o.id);
        setSortBy((prev) => (validIds.includes(prev) ? prev : menu[0].id));
    }, [filter]);

    // Look up enrollment + certificate by course id.
    const enrollmentByCourse = useMemo(() => {
        const map = {};
        (enrollments || []).forEach((e) => { map[e.course_id] = e; });
        return map;
    }, [enrollments]);
    const certByCourse = useMemo(() => {
        const map = {};
        (certificates || []).forEach((c) => { map[c.course_id] = c; });
        return map;
    }, [certificates]);

    // Normalise DB courses into the shape our CourseCard renders.
    const publishedCards = useMemo(() => dbCourses.map((c) => {
        const enrollment = enrollmentByCourse[c.id];
        const cert = certByCourse[c.id];
        // Prerequisite gate — check certificates the doctor holds.
        const prereqIds = c.prerequisite_course_ids || [];
        const heldCertCourseIds = new Set((certificates || []).map((x) => x.course_id));
        const missingPrereqIds = prereqIds.filter((pid) => !heldCertCourseIds.has(pid));
        const prereqTitles = missingPrereqIds.map((pid) => {
            const p = dbCourses.find((x) => x.id === pid);
            return p?.title || "Unknown course";
        });
        const locked = missingPrereqIds.length > 0;

        // Derive status.
        // iter100.157r — After a Retake, `cert` still exists (kept as
        // proof of the first pass) but `enrollment.status` flips back to
        // `enrolled` and lesson_progress is empty. Prioritise enrollment
        // status over `cert` so the card shows "In Progress" for the
        // fresh run instead of "Completed".
        let status = "available";
        if (locked) status = "locked";
        else if (enrollment?.status === "in_progress" || enrollment?.status === "enrolled") status = "in_progress";
        else if (cert || enrollment?.status === "completed") status = "completed";

        return {
            id: c.id,
            title: c.title,
            category: prettyCategory(c.track, c.phase),
            course_type_label: prettyCourseType(c.course_type),
            description: (c.description || "").replace(/<[^>]*>/g, "").slice(0, 180),
            phases: null,
            modules: c.total_modules ?? 0,
            lessons: c.total_lessons ?? 0,
            duration: c.duration_minutes ? `~${Math.max(1, Math.round(c.duration_minutes / 60))} hrs` : null,
            icon: Trophy,
            accent: { tile: "bg-rose-50 text-rose-700 border border-rose-200" },
            tag: c.track ? c.track.toUpperCase() : null,
            status,
            thumbnail_media_id: c.thumbnail_media_id || null,
            href: `/doctor/academy/courses/${c.id}`,
            enrollment,
            cert,
            missingPrereqs: prereqTitles,
            // Pricing (v1: INR only). is_free defaults to true on legacy courses.
            is_free: c.is_free !== false,
            price_amount_cents: c.price_amount_cents ?? 0,
            currency: c.currency || "INR",
            // iter100.156g — Admin-curated pin drives the Recommended tab.
            is_featured: Boolean(c.is_featured),
            // iter101.89 — Data-driven trending signal. Backend counts
            // enrollments per course over the trailing 30 days;
            // `is_trending` is true when the count clears the threshold.
            // Trending courses are unioned into the Recommended tab and
            // get a visible pill on the card banner.
            trending_enroll_count_30d: Number(c.trending_enroll_count_30d || 0),
            is_trending: Number(c.trending_enroll_count_30d || 0) >= TRENDING_ENROLL_THRESHOLD,
        };
    }), [dbCourses, enrollmentByCourse, certByCourse, certificates]);

    const allCards = useMemo(
        () => [...publishedCards, ...COMING_SOON_COURSES],
        [publishedCards],
    );

    const filtered = useMemo(() => {
        if (filter === "certifications") return [];  // handled by CertificationsPanel
        if (filter === "live_sessions") return [];    // handled by LiveSessionsPanel
        let list = allCards;
        if (filter === "in_progress") list = list.filter((c) => c.status === "in_progress");
        else if (filter === "completed") list = list.filter((c) => c.status === "completed");
        else if (filter === "coming_soon") list = list.filter((c) => c.status === "coming_soon");
        else if (filter === "recommended") {
            /* iter101.89 · Recommended is now the union of admin-curated
               (`is_featured`) and data-driven trending (`is_trending`)
               signals. Featured wins ordering so admins keep the top
               spots for their editorial picks; trending non-featured
               courses fill in below; available non-trending courses
               come last so the tab still has content for cold-start
               accounts with no enrollments yet.
               Completed courses are dropped from all three groups so
               Recommended stays a discovery surface, not a history log. */
            const isDiscoverable = (c) => c.status !== "completed";
            const featured = list.filter((c) => c.is_featured && isDiscoverable(c));
            const trending = list.filter((c) => !c.is_featured && c.is_trending && isDiscoverable(c));
            const filler = list.filter((c) =>
                !c.is_featured && !c.is_trending &&
                ["available", "in_progress"].includes(c.status),
            );
            list = [...featured, ...trending, ...filler];
        }
        // "all" shows everything

        if (q.trim()) {
            const needle = q.trim().toLowerCase();
            list = list.filter((c) =>
                (c.title || "").toLowerCase().includes(needle) ||
                (c.description || "").toLowerCase().includes(needle) ||
                (c.category || "").toLowerCase().includes(needle),
            );
        }

        /* iter101.87 · Sort applied last so the chip counts (computed
           against the pre-sort pool) remain accurate. Recommended keeps
           its author-curated order regardless of the sort selection —
           featured first is the whole point of that tab. */
        if (filter !== "recommended") {
            const durationMins = (c) => {
                if (typeof c.duration === "string" && c.duration.startsWith("~")) {
                    const m = c.duration.match(/(\d+)/);
                    if (m) return Number(m[1]) * 60;
                }
                return 0;
            };
            const sorters = {
                newest:      (a, b) => (b.id || "").localeCompare(a.id || ""),
                "title-asc":  (a, b) => (a.title || "").localeCompare(b.title || ""),
                "title-desc": (a, b) => (b.title || "").localeCompare(a.title || ""),
                "shortest":   (a, b) => durationMins(a) - durationMins(b),
                "longest":    (a, b) => durationMins(b) - durationMins(a),
            };
            const cmp = sorters[sortBy] || sorters.newest;
            list = [...list].sort(cmp);
        }
        return list;
    }, [allCards, filter, q, sortBy]);

    const availableCount = publishedCards.length;

    /* iter101.87 · Counts feed the canonical FilterChipRow. Computed
       against `allCards` (pre-search, pre-sort) so switching a chip
       tells the doctor how many things live in each bucket, not how
       many their current search survives. Live Sessions has no cheap
       source of truth at this scope so we hide its count.
       iter101.89 · Recommended count is now the union of admin-featured
       and trending signals (matches the filter body above). */
    const chipCounts = useMemo(() => ({
        all:          allCards.length,
        recommended:  allCards.filter((c) => (c.is_featured || c.is_trending) && c.status !== "completed").length,
        in_progress:  allCards.filter((c) => c.status === "in_progress").length,
        completed:    allCards.filter((c) => c.status === "completed").length,
        certifications: certificates.length,
        coming_soon:  allCards.filter((c) => c.status === "coming_soon").length,
    }), [allCards, certificates]);

    if (loading) {
        return (
            <div className="p-8 max-w-6xl mx-auto space-y-4">
                <div className="h-8 w-56 bg-secondary rounded animate-pulse" />
                <div className="h-32 bg-secondary rounded-2xl animate-pulse" />
                <div className="grid grid-cols-3 gap-4">
                    <div className="h-64 bg-secondary rounded-2xl animate-pulse" />
                    <div className="h-64 bg-secondary rounded-2xl animate-pulse" />
                    <div className="h-64 bg-secondary rounded-2xl animate-pulse" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full" data-testid="academy-catalogue">
            <div className="w-full px-6 md:px-8 py-8">
                <AcademyHero
                    availableCount={availableCount}
                    liveSessionCount={upcomingWebinarCount}
                    certificateCount={certificates.length}
                />

                {/* iter101.87 · Canonical FilterSearchBar + FilterChipRow
                    (same primitive used across /doctor/cases, /doctor/patients,
                    /doctor/billing, /doctor/appointments). Sort dropdown
                    lives in the searchbar's rightSlot; chip row renders
                    below. iter101.90 · Search bar now stays visible on
                    every tab (was hidden on Certifications + Live
                    Sessions before). Both panels accept `query` and
                    `sortBy` props so they filter/sort against the
                    same canonical controls the rest of the page uses. */}
                <div className="mt-6 space-y-2.5" data-testid="academy-controls">
                    <FilterSearchBar
                        query={q}
                        setQuery={setQ}
                        placeholder={SEARCH_PLACEHOLDERS[filter] || SEARCH_PLACEHOLDERS.default}
                        searchTestid="academy-search"
                        testid="academy-filter-bar"
                        rightSlot={<AcademySortMenu sortBy={sortBy} setSortBy={setSortBy} filter={filter} />}
                    />
                    <FilterChipRow
                        value={filter}
                        onChange={setFilter}
                        testid="academy-filter-chips"
                        chipTestidPrefix="academy-filter"
                        chips={ACADEMY_CHIPS.map((c) => ({
                            id:    c.id,
                            label: c.label,
                            count: c.showCount === false ? undefined : chipCounts[c.id],
                            tone:  c.tone,
                            Icon:  c.Icon,
                        }))}
                    />
                </div>

                {/* Body */}
                <div className="mt-6">
                    {filter === "certifications" && (
                        <CertificationsPanel
                            certificates={certificates}
                            enrollments={enrollments}
                            query={q}
                            sortBy={sortBy}
                        />
                    )}
                    {filter === "live_sessions" && (
                        <LiveSessionsPanel query={q} sortBy={sortBy} />
                    )}
                    {filter !== "certifications" && filter !== "live_sessions" && (
                        <CoursesPanel courses={filtered} hasResults={filtered.length > 0} onRefresh={load} />
                    )}
                </div>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   Small premium hero — rose accent (matches "granted by Invisique HQ")
   ───────────────────────────────────────────────────────────────────── */

function AcademyHero({ availableCount, liveSessionCount = 0, certificateCount = 0 }) {
    /* iter101.88 · The single "Live Courses · N" pill on the right was
       replaced by a 3-tile stat strip so the hero surfaces the three
       numbers a doctor actually cares about: how many live courses
       exist, how many live sessions are scheduled, and how many
       certificates they personally hold. Kept in a subdued rose
       palette (translucent white tiles, rose text) so the hero
       doesn't lean any harder into rose than it already does. */
    const stats = [
        { label: "Live Courses",   value: availableCount,   testid: "academy-stat-courses" },
        { label: "Live Sessions",  value: liveSessionCount, testid: "academy-stat-sessions" },
        { label: "Certifications", value: certificateCount, testid: "academy-stat-certs" },
    ];
    return (
        <div
            className="relative overflow-hidden rounded-2xl bento-shadow bg-gradient-to-br from-rose-50 via-white to-white border border-rose-100 px-6 md:px-8 py-6"
            data-testid="academy-hero"
        >
            {/* Decorative rose spark on right */}
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-rose-100/40 blur-2xl" aria-hidden="true" />
            <div className="absolute top-4 right-6 opacity-60" aria-hidden="true">
                <Sparkle size={20} weight="fill" className="text-rose-400" />
            </div>
            <div className="absolute top-14 right-14 opacity-50" aria-hidden="true">
                <Sparkle size={10} weight="fill" className="text-rose-500" />
            </div>

            <div className="relative flex items-start gap-4 flex-wrap md:flex-nowrap">
                <div className="w-12 h-12 rounded-xl bg-rose-500 border border-rose-600 flex items-center justify-center shrink-0 shadow-[0_2px_8px_rgba(225,29,72,0.25)]">
                    <GraduationCap size={24} weight="fill" className="text-white" />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="overline text-rose-600" data-testid="academy-overline">Invisique Academy</div>
                    <h1 className="font-display text-2xl sm:text-[26px] font-bold tracking-tight text-foreground mt-0.5" data-testid="academy-title">
                        Invisique Learning Academy
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-xl" data-testid="academy-subtitle">
                        Expand your skills, earn certifications, and grow your practice with Invisique™.
                    </p>
                </div>

                <div
                    className="hidden md:grid grid-cols-3 gap-2 shrink-0 self-stretch"
                    data-testid="academy-stat-strip"
                >
                    {stats.map((s) => (
                        <div
                            key={s.label}
                            className="min-w-[92px] flex flex-col items-start justify-center px-3.5 py-2.5 rounded-xl bg-white/70 border border-rose-200/70 backdrop-blur-sm shadow-[0_1px_4px_rgba(225,29,72,0.06)]"
                            data-testid={s.testid}
                        >
                            <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-rose-600 whitespace-nowrap">
                                {s.label}
                            </div>
                            <div className="font-display text-2xl font-black text-rose-700 tabular-nums leading-none mt-1">
                                {s.value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Mobile-only stat strip: matches the desktop tile visual
                exactly, just wraps below the hero copy instead of
                sitting on the right. */}
            <div className="md:hidden mt-4 grid grid-cols-3 gap-2" data-testid="academy-stat-strip-mobile">
                {stats.map((s) => (
                    <div
                        key={s.label}
                        className="flex flex-col items-start justify-center px-3 py-2 rounded-xl bg-white/70 border border-rose-200/70 backdrop-blur-sm"
                    >
                        <div className="text-[8px] font-bold uppercase tracking-[0.1em] text-rose-600 whitespace-nowrap">
                            {s.label}
                        </div>
                        <div className="font-display text-xl font-black text-rose-700 tabular-nums leading-none mt-1">
                            {s.value}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   Courses grid
   ───────────────────────────────────────────────────────────────────── */

function CoursesPanel({ courses, hasResults, onRefresh }) {
    if (!hasResults) {
        return (
            <div className="col-span-full border border-dashed border-border bg-white rounded-2xl p-10 text-center" data-testid="academy-empty">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 mx-auto mb-3 grid place-items-center">
                    <Sparkle size={28} weight="duotone" className="text-primary" />
                </div>
                <h3 className="font-display text-lg font-bold text-foreground">No courses match your filters</h3>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
                    Try switching the filter or clearing your search — new courses ship regularly.
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="academy-course-grid">
            {courses.map((c) => <CourseCard key={c.id} course={c} onRefresh={onRefresh} />)}
        </div>
    );
}


function CourseCard({ course, onRefresh }) {
    const [payDlgOpen, setPayDlgOpen] = useState(false);
    const [retakeDlgOpen, setRetakeDlgOpen] = useState(false);
    const statusMeta = STATUS_META[course.status] || STATUS_META.available;
    const isComingSoon = course.status === "coming_soon";
    const isLocked = course.status === "locked";
    const isCompleted = course.status === "completed";
    const isInProgress = course.status === "in_progress";
    const percent = course.enrollment ? computeCoursePercent(course.enrollment) : 0;

    // A course is "paid + not yet purchased" when it's paid, doctor has no
    // active enrollment, and they aren't in the completed/locked/coming-soon
    // buckets. This trips the Contact-CoE dialog instead of opening the player.
    const priceRupees = course.is_free ? 0 : Math.round((course.price_amount_cents || 0) / 100);
    const isPaidUnpurchased = !course.is_free && !isInProgress && !isCompleted && !isLocked && !isComingSoon;

    let ctaLabel = "Start Course";
    if (isComingSoon) ctaLabel = "Coming Soon";
    else if (isLocked) ctaLabel = "Locked";
    else if (isCompleted) ctaLabel = "View Certificate";
    else if (isInProgress) ctaLabel = "Continue Course";
    else if (isPaidUnpurchased) ctaLabel = `Enroll — ₹${priceRupees.toLocaleString("en-IN")}`;

    const cardCls = isComingSoon || isLocked
        ? "opacity-95"
        : "hover:shadow-[0_12px_32px_rgba(0,45,57,0.10)] hover:-translate-y-1";

    return (
        <div
            className={`group relative bg-white rounded-2xl bento-shadow p-5 flex flex-col h-full transition-all duration-300 ${cardCls}`}
            data-testid={`academy-course-${course.id}`}
        >
            {/* iter101.88 · Always render the top image band. Real
                thumbnails still get shown as-is (unchanged). Cards
                without a `thumbnail_media_id` now get a taller
                gradient fallback that surfaces the course.icon at
                a hero-worthy size plus the status pill floated in
                the top-right of the banner. */}
            <CardThumbnail
                mediaId={course.thumbnail_media_id}
                course={course}
                statusMeta={statusMeta}
                statusTestid={`academy-course-status-${course.id}`}
            />

            {course.course_type_label && (
                <div className="mt-1">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700 text-[11px] font-semibold" data-testid={`academy-course-type-${course.id}`}>
                        {course.course_type_label}
                    </span>
                </div>
            )}

            <div className="mt-3">
                <h3 className="font-display text-[15px] font-bold text-foreground leading-snug mt-1 line-clamp-2 min-h-[2.4em]" data-testid={`academy-course-title-${course.id}`}>
                    {course.title}
                </h3>
            </div>

            {course.description && (
                <p className="text-[13px] text-muted-foreground leading-relaxed mt-2 line-clamp-3">
                    {course.description}
                </p>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                {!isComingSoon && (course.is_free ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider" data-testid={`academy-course-price-${course.id}`}>
                        Free
                    </span>
                ) : (
                    <span className="inline-flex items-baseline gap-0.5 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-[11px] font-black tabular-nums" data-testid={`academy-course-price-${course.id}`}>
                        <span className="text-[10px] font-bold">₹</span>{priceRupees.toLocaleString("en-IN")}
                    </span>
                ))}
                {course.phases != null && (
                    <span className="inline-flex items-center gap-1"><Stack size={12} weight="duotone" /> {course.phases} phases</span>
                )}
                <span className="inline-flex items-center gap-1">
                    <Books size={12} weight="duotone" /> {course.modules} modules
                </span>
                {course.duration && (
                    <span className="inline-flex items-center gap-1"><Clock size={12} weight="duotone" /> {course.duration}</span>
                )}
            </div>

            {/* Progress bar for in-progress + completed */}
            {(isInProgress || isCompleted) && course.enrollment && (
                <div className="mt-3">
                    <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                        <span>{isCompleted ? "Complete" : "Progress"}</span>
                        <strong className="text-foreground tabular-nums">{isCompleted ? 100 : percent}%</strong>
                    </div>
                    <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${isCompleted ? "bg-emerald-500" : "bg-rose-500"}`}
                            style={{ width: `${isCompleted ? 100 : percent}%` }}
                            data-testid={`academy-course-progress-${course.id}`}
                        />
                    </div>
                </div>
            )}

            {/* Locked hint */}
            {isLocked && course.missingPrereqs.length > 0 && (
                <div className="mt-3 px-2.5 py-1.5 rounded-md bg-slate-100 border border-slate-200 text-[11px] text-slate-700" data-testid={`academy-course-locked-hint-${course.id}`}>
                    <Lock size={11} weight="fill" className="inline mr-1 -mt-0.5" />
                    Complete first: <span className="font-semibold">{course.missingPrereqs.join(", ")}</span>
                </div>
            )}

            {/* CTA */}
            <div className="mt-auto pt-4">
                {isComingSoon ? (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled
                        className="w-full h-10 font-semibold border-border text-muted-foreground bg-secondary/40 hover:bg-secondary/40"
                        data-testid={`academy-course-cta-${course.id}`}
                    >
                        <Lock size={14} weight="duotone" className="mr-1.5" />
                        {ctaLabel}
                    </Button>
                ) : isLocked ? (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled
                        className="w-full h-10 font-semibold border-slate-200 text-slate-500 bg-slate-50"
                        data-testid={`academy-course-cta-${course.id}`}
                    >
                        <Lock size={14} weight="fill" className="mr-1.5" />
                        {ctaLabel}
                    </Button>
                ) : isCompleted && course.cert ? (
                    <>
                        <Button
                            size="sm"
                            className="w-full h-10 font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-sm hover:shadow-md transition-all"
                            onClick={() => downloadCertificate(course.cert.id)}
                            data-testid={`academy-course-cta-${course.id}`}
                        >
                            <Certificate size={14} weight="fill" className="mr-1.5" />
                            {ctaLabel}
                        </Button>
                        <button
                            type="button"
                            onClick={() => setRetakeDlgOpen(true)}
                            className="mt-2 w-full text-center text-[11px] font-medium text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                            data-testid={`academy-course-retake-${course.id}`}
                        >
                            Retake course →
                        </button>
                        <RetakeCourseDialog
                            open={retakeDlgOpen}
                            onOpenChange={setRetakeDlgOpen}
                            courseTitle={course.title}
                            onConfirm={async () => {
                                try {
                                    await api.post(`/academy/courses/${course.id}/reset`);
                                    toast.success("Course reset. Opening a fresh run…");
                                    window.open(course.href, "_blank", "noopener");
                                    // Refresh the catalogue so the card flips to In Progress.
                                    onRefresh?.();
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Couldn't reset the course.");
                                    throw e;
                                }
                            }}
                        />
                    </>
                ) : (
                    <>
                        {isPaidUnpurchased ? (
                            <>
                                <Button
                                    size="sm"
                                    className="w-full h-10 font-semibold text-white bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 shadow-sm hover:shadow-md transition-all"
                                    onClick={async () => {
                                        // Try to enrol server-side. If the payment gate is
                                        // disabled (checkout not wired yet), the backend
                                        // returns 200 and we open the player. If enabled,
                                        // it returns 402 and we fall back to the
                                        // Contact-CoE dialog.
                                        try {
                                            await api.post(`/academy/courses/${course.id}/enroll`);
                                            window.open(course.href, "_blank", "noopener");
                                        } catch (e) {
                                            if (e?.response?.status === 402) {
                                                setPayDlgOpen(true);
                                            } else {
                                                toast.error(e?.response?.data?.detail || "Couldn't enroll — please try again.");
                                            }
                                        }
                                    }}
                                    data-testid={`academy-course-cta-${course.id}`}
                                >
                                    <span className="inline-flex items-center gap-1.5">
                                        {ctaLabel}
                                    </span>
                                </Button>
                                <div className="mt-2 text-center text-[10px] font-medium text-muted-foreground/70">
                                    Instant enroll · Certificate on completion
                                </div>
                                <PaymentPendingDialog
                                    open={payDlgOpen}
                                    onOpenChange={setPayDlgOpen}
                                    courseTitle={course.title}
                                    priceRupees={priceRupees}
                                />
                            </>
                        ) : (
                            <>
                                <Button
                                    asChild
                                    size="sm"
                                    className="w-full h-10 font-semibold text-white bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 shadow-sm hover:shadow-md transition-all"
                                    data-testid={`academy-course-cta-${course.id}`}
                                >
                                    <Link to={course.href} className="no-underline text-inherit" target="_blank" rel="noopener">
                                        <span className="inline-flex items-center gap-1.5">
                                            {ctaLabel}
                                            <ArrowSquareOut size={14} weight="bold" />
                                        </span>
                                    </Link>
                                </Button>
                                <div className="mt-2 text-center text-[10px] font-medium text-muted-foreground/70">
                                    Opens the course in a new tab
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}


function computeCoursePercent(enrollment) {
    // Best-effort client-side percent — server also computes summary.percent
    // but that requires a per-course fetch. Here we approximate from the
    // enrollment shape so cards can show a bar without an extra roundtrip.
    if (!enrollment) return 0;
    const lp = enrollment.lesson_progress || {};
    const lessonIds = Object.keys(lp);
    if (lessonIds.length === 0) return 0;
    const viewed = lessonIds.filter((id) => lp[id]?.viewed_at).length;
    const quizPassed = lessonIds.filter((id) => lp[id]?.quiz?.passed).length;
    const total = lessonIds.length;
    const raw = ((viewed * 0.6) + (quizPassed * 0.3)) / total;
    return Math.min(99, Math.max(5, Math.round(raw * 100)));
}


/*  Certificate download helper — auth-checked blob → open in new tab. */
async function downloadCertificate(certId) {
    try {
        const resp = await api.get(`/academy/certificates/${certId}`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        window.open(url, "_blank");
    } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't fetch certificate.");
    }
}


/*  iter100.113s — Copy the public verify URL to clipboard. Doctors
    typically paste this into LinkedIn / their bio — the verify page is
    a growth loop landing that pulls prospects back to Invisique. */
async function copyShareLink(certNumber) {
    if (!certNumber) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/verify/${certNumber}`;
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
            toast.success("Verify link copied — perfect for LinkedIn.");
        } else {
            // Fallback: open in a new tab so the doctor can copy manually.
            window.open(url, "_blank");
        }
    } catch {
        window.open(url, "_blank");
    }
}


/*  Certifications panel — table of issued certs with renewal + download. */
function CertificationsPanel({ certificates, enrollments, query = "", sortBy = "issued-desc" }) {
    // Build a course_id → enrollment lookup so each cert row can compute
    // attempt count (`reset_history.length + 1`) and best score.
    const enrollmentByCourse = useMemo(() => {
        const map = {};
        (enrollments || []).forEach((e) => { map[e.course_id] = e; });
        return map;
    }, [enrollments]);

    /* iter101.90 · Apply the shared search + sort from the canonical
       controls at the parent scope. Filter first (so summary counts
       reflect what's visible), then sort. */
    const visibleCerts = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        let list = certificates || [];
        if (q) {
            list = list.filter((c) => (c.course_title || "").toLowerCase().includes(q));
        }
        const parseDate = (v) => (v ? new Date(v).getTime() : 0);
        const sorters = {
            "issued-desc": (a, b) => parseDate(b.issued_at) - parseDate(a.issued_at),
            "issued-asc":  (a, b) => parseDate(a.issued_at) - parseDate(b.issued_at),
            "expires-asc": (a, b) => parseDate(a.expires_at) - parseDate(b.expires_at),
            "title-asc":   (a, b) => (a.course_title || "").localeCompare(b.course_title || ""),
        };
        const cmp = sorters[sortBy] || sorters["issued-desc"];
        return [...list].sort(cmp);
    }, [certificates, query, sortBy]);

    if (!certificates || certificates.length === 0) {
        return (
            <div className="bg-white rounded-2xl bento-shadow p-12 text-center" data-testid="academy-cert-empty-state">
                <div className="w-14 h-14 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-3">
                    <Trophy size={28} weight="duotone" className="text-rose-500" />
                </div>
                <h3 className="font-display text-lg font-bold text-foreground mb-1">No certificates yet</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                    Complete a course to earn your first Invisique&trade; certificate — downloadable as a PDF and valid for {CERT_VALIDITY_DAYS} days.
                </p>
            </div>
        );
    }

    // Summary tiles + renewal alert reflect the FULL cert list (not
    // filtered by search) so the doctor doesn't get scared by a
    // search-narrowed "0 active" count when they actually have some.
    const active = certificates.filter((c) => c.status === "active" || c.status === "expiring");
    const expiring = certificates.filter((c) => c.status === "expiring");
    const nearest = expiring.length ? expiring[0] : null;

    return (
        <div className="space-y-4" data-testid="academy-cert-panel">
            {nearest && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-center gap-3" data-testid="academy-cert-renewal-alert">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
                        <Warning size={16} weight="fill" className="text-amber-700" />
                    </div>
                    <div className="text-xs text-amber-900">
                        <strong>Renewal reminder:</strong> {nearest.course_title} expires on <strong>{formatDate(nearest.expires_at)}</strong>.
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SummaryCard icon={Trophy}       tint="primary" num={active.length}   label={active.length === 1 ? "Active certificate" : "Active certificates"} />
                <SummaryCard icon={Hourglass}    tint="slate"   num={certificates.length - active.length} label="Expired" />
                <SummaryCard icon={CalendarBlank} tint="amber"  num={nearest ? daysUntil(nearest.expires_at) : "—"} label="Days to nearest renewal" />
            </div>

            <div className="bg-white rounded-2xl bento-shadow overflow-hidden">
                <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <h3 className="font-display text-sm font-bold text-foreground">My Certificates</h3>
                    <span className="text-[11px] text-muted-foreground" data-testid="academy-cert-visible-count">
                        Showing {visibleCerts.length} of {certificates.length}
                    </span>
                </div>
                <div className="overflow-x-auto">
                    {visibleCerts.length === 0 ? (
                        <div className="px-5 py-10 text-center" data-testid="academy-cert-noresults">
                            <MagnifyingGlass size={28} weight="duotone" className="text-muted-foreground/60 mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                                No certificates match &ldquo;{query}&rdquo;.
                            </p>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="bg-secondary/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                                    <th className="text-left px-5 py-2.5">Certificate</th>
                                    <th className="text-left px-3 py-2.5">Issued</th>
                                    <th className="text-left px-3 py-2.5">Expires</th>
                                    <th className="text-left px-3 py-2.5">Status</th>
                                    <th className="text-right px-5 py-2.5">Action</th>
                                </tr>
                            </thead>
                            <tbody data-testid="academy-cert-table-body">
                                {visibleCerts.map((c) => <CertRow key={c.id} cert={c} enrollment={enrollmentByCourse[c.course_id]} />)}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}


function SummaryCard({ icon: Icon, tint, num, label }) {
    const tintCls =
        tint === "primary" ? "bg-rose-50 text-rose-700 border-rose-200"
        : tint === "amber" ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-slate-100 text-slate-500 border-slate-200";
    return (
        <div className="bg-white rounded-2xl bento-shadow px-4 py-3.5 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${tintCls}`}>
                <Icon size={20} weight="duotone" />
            </div>
            <div>
                <div className="font-display text-xl font-bold text-foreground leading-none tabular-nums">{num}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
            </div>
        </div>
    );
}


function CertRow({ cert, enrollment }) {
    const pill = cert.status === "expiring"
        ? { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Expiring soon", icon: Warning }
        : cert.status === "expired"
            ? { cls: "bg-red-50 text-red-800 border-red-200", label: "Expired", icon: Warning }
            : { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Active", icon: CheckCircle };
    const PillIcon = pill.icon;

    // iter100.157r — Retake stats. Attempt count = past resets + current run.
    // Best score prefers final_exam.best_score (that's what the certificate
    // was awarded on); falls back to reset_history's stored prev_percent.
    const resetCount = (enrollment?.reset_history || []).length;
    const attempts = resetCount + 1;
    const bestFinalScore = enrollment?.final_exam?.best_score;
    const bestPercent = typeof bestFinalScore === "number"
        ? Math.round(bestFinalScore * 100)
        : Math.max(
            100,
            ...(enrollment?.reset_history || []).map((r) => r.prev_percent || 0),
        );
    const showRetakeChip = attempts > 1;

    return (
        <tr className="border-t border-border/60 hover:bg-secondary/30 transition-colors" data-testid={`academy-cert-row-${cert.id}`}>
            <td className="px-5 py-3">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg border bg-rose-50 text-rose-500 border-rose-200 flex items-center justify-center shrink-0">
                        <Certificate size={16} weight="fill" />
                    </div>
                    <div>
                        <div className="text-[13px] font-semibold text-foreground flex items-center gap-2 flex-wrap">
                            <span>{cert.course_title}</span>
                            {showRetakeChip && (
                                <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
                                    title={`Completed ${attempts}× — great signal of continued mastery.`}
                                    data-testid={`academy-cert-retake-chip-${cert.id}`}
                                >
                                    <ClockCounterClockwise size={10} weight="bold" />
                                    {attempts}× · best {bestPercent}%
                                </span>
                            )}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                            {cert.cert_number || cert.id.slice(0, 8)}
                            {cert.ce_credits ? <> · {cert.ce_credits} CE</> : null}
                        </div>
                    </div>
                </div>
            </td>
            <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(cert.issued_at)}</td>
            <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(cert.expires_at)}</td>
            <td className="px-3 py-3">
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${pill.cls}`}>
                    <PillIcon size={11} weight="fill" /> {pill.label}
                </span>
            </td>
            <td className="px-5 py-3 text-right">
                <div className="inline-flex items-center gap-1.5" data-testid={`academy-cert-actions-${cert.id}`}>
                    <button
                        type="button"
                        onClick={() => copyShareLink(cert.cert_number)}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-md transition-colors"
                        data-testid={`academy-cert-share-${cert.id}`}
                        title="Copy public verify link — great for LinkedIn"
                    >
                        <Share size={12} weight="bold" /> Share
                    </button>
                    <button
                        type="button"
                        onClick={() => downloadCertificate(cert.id)}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-2.5 py-1 rounded-md transition-colors"
                        data-testid={`academy-cert-download-${cert.id}`}
                    >
                        <DownloadSimple size={12} weight="bold" /> PDF
                    </button>
                </div>
            </td>
        </tr>
    );
}


function formatDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return "—"; }
}


function daysUntil(iso) {
    if (!iso) return "—";
    try {
        const days = Math.max(0, Math.round((new Date(iso) - new Date()) / 86400000));
        return days;
    } catch { return "—"; }
}


/*  Thumbnail — auth-checked blob fetch. Displayed at the very top of
    the card, above the icon-tile header row. Falls back silently to
    a plain teal illustration if the fetch fails.  */
/* iter101.88 · Top image band. Real thumbnails render at h-28
   (unchanged). Cards without a thumbnail now render a taller h-36
   gradient fallback that uses the course's own accent palette,
   surfaces `course.icon` at 48px, and floats the status pill in
   the top-right — matches the reference layout while staying on
   canonical Phosphor iconography (no emoji). */
function CardThumbnail({ mediaId, course, statusMeta, statusTestid }) {
    const [blobUrl, setBlobUrl] = useState(null);
    useEffect(() => {
        let cancelled = false;
        let obj = null;
        if (!mediaId) { setBlobUrl(null); return undefined; }
        (async () => {
            try {
                const token = localStorage.getItem("ca_token");
                const resp = await fetch(`${API}/course-builder/media/${mediaId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!resp.ok) throw new Error("fetch failed");
                const blob = await resp.blob();
                obj = URL.createObjectURL(blob);
                if (!cancelled) setBlobUrl(obj);
            } catch { /* silent fallback */ }
        })();
        return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
    }, [mediaId]);

    const Icon = course?.icon || GraduationCap;
    const statusPill = statusMeta ? (
        <span
            className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full whitespace-nowrap shadow-sm ${statusMeta.cls}`}
            data-testid={statusTestid}
        >
            {statusMeta.label}
        </span>
    ) : null;

    /* iter101.89 · Trending overlay. Rendered on both real-photo and
       fallback banners at the bottom-left so it never collides with
       the top-right status pill. Hot rose gradient + Fire icon signals
       "this is hot right now" without needing a legend. Threshold is
       enforced in the parent (`is_trending`) so this component only
       gets the flag + raw count and just decides how to render. */
    const trendingPill = course?.is_trending ? (
        <span
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full whitespace-nowrap text-white bg-gradient-to-r from-rose-500 to-rose-600 shadow-[0_2px_8px_rgba(225,29,72,0.35)]"
            title={`${course.trending_enroll_count_30d} doctors enrolled in the last 30 days`}
            data-testid={`academy-course-trending-${course.id}`}
        >
            <Fire size={11} weight="fill" />
            Trending · {course.trending_enroll_count_30d}
        </span>
    ) : null;

    // Real photo path (unchanged h-28, plus the status pill overlay).
    if (blobUrl) {
        return (
            <div className="relative h-28 -mx-5 -mt-5 mb-3 overflow-hidden rounded-t-2xl border-b border-border">
                <img src={blobUrl} alt="Course thumbnail" className="w-full h-full object-cover" />
                {statusPill}
                {trendingPill}
            </div>
        );
    }

    // Fallback banner — taller, richer, uses the course's accent
    // palette so the visual language stays in the rose/teal/amber
    // family the card already establishes.
    const bandTint = course?.accent?.tile || "bg-rose-50 text-rose-700 border border-rose-200";
    return (
        <div
            className={`relative h-36 -mx-5 -mt-5 mb-3 overflow-hidden rounded-t-2xl border-b border-border ${bandTint}`}
            data-testid={course ? `academy-course-thumb-${course.id}` : undefined}
        >
            {/* Decorative sparkle blur — same visual treatment as the hero */}
            <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/50 blur-2xl" aria-hidden="true" />
            <div className="absolute top-3 left-4 opacity-70" aria-hidden="true">
                <Sparkle size={12} weight="fill" />
            </div>
            <div className="absolute bottom-3 right-6 opacity-40" aria-hidden="true">
                <Sparkle size={8} weight="fill" />
            </div>
            <div className="relative w-full h-full flex items-center justify-center">
                <Icon size={56} weight="duotone" />
            </div>
            {statusPill}
            {trendingPill}
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   Helpers
   ───────────────────────────────────────────────────────────────────── */

function prettyCategory(track, phase) {
    if (track && phase) return `${prettyTrack(track)} · ${prettyPhase(phase)}`;
    if (track) return prettyTrack(track);
    if (phase) return prettyPhase(phase);
    return "Certification Program";
}

function prettyTrack(t) {
    if (!t) return "";
    const map = { gp: "General Dentist", ortho: "Orthodontist", pediatric: "Pediatric", both: "All Tracks" };
    return map[t.toLowerCase()] || t;
}

function prettyPhase(p) {
    if (!p) return "";
    const map = {
        foundation: "Phase 1 · Foundation",
        assessment: "Phase 2 · Assessment",
        validation: "Phase 3 · Validation",
    };
    return map[p.toLowerCase()] || p;
}

function prettyCourseType(t) {
    if (!t) return null;
    const map = {
        foundation_modules:         "Foundation Modules",
        starter_case_workshop:      "Starter Case Workshop",
        clinical_planning_workshop: "Clinical Planning Workshop",
        provider_growth_program:    "Provider Growth Program",
    };
    return map[t] || null;
}


/* ═════════════════════════════════════════════════════════════════════
   iter100.156f — Live Sessions / Webinars
   External-URL webinar RSVP + join card grid.
   ═════════════════════════════════════════════════════════════════════ */

function LiveSessionsPanel({ query = "", sortBy = "soonest" }) {
    const [data, setData] = useState({ upcoming: [], past: [] });
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: resp } = await api.get("/academy/webinars");
            setData({ upcoming: resp.upcoming || [], past: resp.past || [] });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load live sessions.");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const rsvp = useCallback(async (webinar) => {
        try {
            const { data: resp } = await api.post(`/academy/webinars/${webinar.id}/rsvp`);
            setData((prev) => {
                const patch = (row) => row.id === webinar.id
                    ? { ...row, is_rsvped: resp.is_rsvped, rsvp_count: resp.rsvp_count }
                    : row;
                return {
                    upcoming: prev.upcoming.map(patch),
                    past: prev.past.map(patch),
                };
            });
            toast.success(resp.is_rsvped ? "You're RSVP'd. We'll remind you closer to the session." : "RSVP withdrawn.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update RSVP.");
        }
    }, []);

    /* iter101.90 · Apply the shared search + sort from the canonical
       controls at the parent scope. Matches title, presenter name, and
       description so a doctor can find a session by topic ("bio…"),
       speaker ("dr. patel"), or track ("planning"). */
    const { upcomingVisible, pastVisible } = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        const matches = (w) => {
            if (!q) return true;
            const hay = [
                w.title, w.presenter_name, w.presenter, w.description,
                w.topic, w.track,
            ].map((v) => (v || "").toLowerCase()).join(" ");
            return hay.includes(q);
        };
        const parseDate = (v) => (v ? new Date(v).getTime() : 0);
        const upcomingSorters = {
            soonest:  (a, b) => parseDate(a.start_at) - parseDate(b.start_at),
            furthest: (a, b) => parseDate(b.start_at) - parseDate(a.start_at),
            newest:   (a, b) => parseDate(b.created_at || b.start_at) - parseDate(a.created_at || a.start_at),
        };
        // Past recordings — "soonest" doesn't apply, so on past we
        // always default to "newest posted" (most recent recording
        // first) regardless of the selected upcoming sort. Keeps the
        // past section useful without polluting the sort menu with
        // separate past/upcoming controls.
        const cmpUpcoming = upcomingSorters[sortBy] || upcomingSorters.soonest;
        const cmpPast = (a, b) => parseDate(b.start_at) - parseDate(a.start_at);
        return {
            upcomingVisible: [...(data.upcoming || [])].filter(matches).sort(cmpUpcoming),
            pastVisible:     [...(data.past || [])].filter(matches).sort(cmpPast),
        };
    }, [data, query, sortBy]);

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-40 bg-secondary rounded-2xl animate-pulse" />
                ))}
            </div>
        );
    }

    const isSearching = !!(query || "").trim();
    const noMatches = isSearching && upcomingVisible.length === 0 && pastVisible.length === 0;

    return (
        <div className="space-y-8" data-testid="live-sessions-panel">
            {noMatches && (
                <div className="bg-white rounded-2xl bento-shadow p-10 text-center" data-testid="live-sessions-noresults">
                    <MagnifyingGlass size={28} weight="duotone" className="text-muted-foreground/60 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                        No live sessions match &ldquo;{query}&rdquo;.
                    </p>
                </div>
            )}
            {!noMatches && (
                <section>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-lg font-bold flex items-center gap-2">
                            <Broadcast size={18} weight="fill" className="text-rose-500" />
                            Upcoming <span className="text-muted-foreground font-normal">· {upcomingVisible.length}</span>
                        </h2>
                    </div>
                    {upcomingVisible.length === 0 ? (
                        <div className="border border-dashed border-border bg-white rounded-2xl p-10 text-center">
                            <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 mx-auto mb-3 grid place-items-center">
                                <CalendarBlank size={24} weight="duotone" className="text-rose-600" />
                            </div>
                            <h3 className="font-display text-lg font-bold">
                                {isSearching ? "No upcoming sessions match your search" : "No live sessions scheduled"}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
                                {isSearching
                                    ? "Try a different keyword, or clear the search to see all upcoming sessions."
                                    : "Our clinical faculty and industry experts host regular webinars on aligner planning, biomechanics, and practice growth. Check back soon."}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {upcomingVisible.map((w) => <SessionCard key={w.id} webinar={w} kind="upcoming" onRsvp={rsvp} />)}
                        </div>
                    )}
                </section>
            )}

            {!noMatches && pastVisible.length > 0 && (
                <section>
                    <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
                        <PlayCircle size={18} weight="fill" className="text-muted-foreground" />
                        Past recordings <span className="text-muted-foreground font-normal">· {pastVisible.length}</span>
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {pastVisible.map((w) => <SessionCard key={w.id} webinar={w} kind="past" onRsvp={rsvp} />)}
                    </div>
                </section>
            )}
        </div>
    );
}


function SessionCard({ webinar, kind, onRsvp }) {
    const start = webinar.start_at ? new Date(webinar.start_at) : null;
    const dateLabel = start ? start.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
    const AUDIENCE_LABEL = {
        all: "All doctors",
        orthodontist: "Orthodontists",
        general_dentist: "General Dentists",
    };
    const featured = webinar.featured;

    return (
        <article
            className={`rounded-2xl border p-5 bg-card hover:shadow-md transition-all ${featured ? "border-rose-200 bg-rose-50/40" : "border-border"}`}
            data-testid={`session-card-${webinar.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-rose-500 text-white grid place-items-center shrink-0 shadow-sm">
                    <VideoCamera size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        {featured && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500 text-white">
                                <Sparkle size={9} weight="fill" className="inline mr-0.5" /> Featured
                            </span>
                        )}
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            {AUDIENCE_LABEL[webinar.audience] || "Everyone"} · {webinar.duration_minutes} min
                        </span>
                    </div>
                    <h3 className="font-display font-bold text-base leading-tight" data-testid={`session-title-${webinar.id}`}>
                        {webinar.title}
                    </h3>
                    {webinar.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{webinar.description}</p>
                    )}
                    <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><CalendarBlank size={11} weight="fill" /> {dateLabel}</span>
                        <span className="inline-flex items-center gap-1"><Users size={11} weight="fill" /> {webinar.host_name}</span>
                        {webinar.rsvp_count > 0 && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                <CheckCircle size={11} weight="fill" /> {webinar.rsvp_count} RSVP&apos;d
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
                {kind === "upcoming" && (
                    <>
                        <button
                            type="button"
                            onClick={() => onRsvp(webinar)}
                            className={`h-9 px-3 rounded-md text-xs font-bold transition-colors ${
                                webinar.is_rsvped
                                    ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-50"
                                    : "border border-border bg-secondary hover:bg-secondary/70"
                            }`}
                            data-testid={`session-rsvp-${webinar.id}`}
                        >
                            {webinar.is_rsvped ? "✓ RSVP\u2019d" : "RSVP"}
                        </button>
                        <a
                            href={webinar.join_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-9 px-3 rounded-md bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold inline-flex items-center gap-1.5 no-underline transition-colors"
                            data-testid={`session-join-${webinar.id}`}
                        >
                            Join session <ArrowSquareOut size={11} weight="bold" />
                        </a>
                    </>
                )}
                {kind === "past" && (
                    webinar.recording_url ? (
                        <a
                            href={webinar.recording_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-9 px-3 rounded-md bg-primary hover:bg-primary/90 text-white text-xs font-bold inline-flex items-center gap-1.5 no-underline transition-colors"
                            data-testid={`session-recording-${webinar.id}`}
                        >
                            <PlayCircle size={12} weight="fill" /> Watch recording
                        </a>
                    ) : (
                        <span className="text-[11px] italic text-muted-foreground">Recording pending upload</span>
                    )
                )}
            </div>
        </article>
    );
}

